// src/features/groups/components/GroupList.tsx

import React from 'react';
import {
  List,
  TextField,
  FunctionField,
  useTranslate,
  TextInput,
  ListProps,
} from 'react-admin';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import { Business, Group as GroupIcon, Hotel } from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import GroupActions from './GroupActions';
import { useGroupPermissions } from '../hooks/useGroupPermissions';
import { Group } from '../types';
import CustomListToolbar from '@/components/common/CustomListToolbar';
import { CustomTextInputFilter } from '@/components/common/CustomFilters';

// Filtros para la lista
const GroupFilters = [
  <CustomTextInputFilter 
    key="search"
    source="search" 
    label="resources.groups.list.search" 
    alwaysOn 
    placeholder="resources.groups.list.search_placeholder"
  />,
];

// Componente principal de lista
export const GroupList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const theme = useTheme();
  const { canViewGroups, canEditGroups } = useGroupPermissions();

  // Si no tiene permisos, mostrar mensaje
  if (!canViewGroups) {
    return (
      <Box 
        sx={{ 
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}
      >
        <Business sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {translate('ra.page.forbidden', 'Acceso Denegado')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.groups.access_denied', 'Solo los SuperAdmins pueden gestionar grupos.')}
        </Typography>
      </Box>
    );
  }

  return (
    <List
      {...props}
      title={translate('resources.groups.list.title', 'Grupos')}
      actions={ <CustomListToolbar
          showFilterButton={false} 
          showCreate={canEditGroups} 
          createLabel={translate('resources.groups.actions.create', 'Nuevo Grupo')}
        />}
      filters={GroupFilters}
      perPage={25}
      sort={{ field: 'name', order: 'ASC' }}
      filterDefaultValues={{ isActive: true }}
      empty={
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <GroupIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {translate('resources.groups.empty', 'No hay grupos')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.groups.empty_help', 'Crea tu primer grupo para comenzar')}
          </Typography>
        </Box>
      }
     
    >

      <Box>
        <CustomDataGrid
          bulkActionButtons={canEditGroups ? undefined : false}
          rowClick={canEditGroups ? "edit" : "show"}
        >
        {/* Columna de acciones */}
        <FunctionField
          label=""
          render={(record: Group) => <GroupActions record={record} />}
          sortable={false}
        />

        {/* Nombre del grupo */}
        <TextField 
          source="name" 
          label={translate('resources.groups.fields.name', 'Nombre')}
          sortable={true}
          sx={{ fontWeight: 600 }}
        />

        {/* Descripción */}
        <FunctionField
          label={translate('resources.groups.fields.description', 'Descripción')}
          render={(record: Group) => (
            <Typography 
              variant="body2" 
              sx={{ 
                overflow: 'auto',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {record.description || '-'}
            </Typography>
          )}
          sortable={false}
        />

        {/* Estadísticas */}
        <FunctionField
          label={translate('resources.groups.fields.hotels', 'Hoteles')}
          render={(record: Group) => (
            <Chip
              icon={<Hotel />}
              label={record.hotelCount || 0}
              size="small"
              variant="outlined"
              color={record.hotelCount > 0 ? "primary" : "default"}
            />
          )}
          sortable={false}
        />

       

        {/* Fecha de creación */}
        <FunctionField
          label={translate('resources.groups.fields.createdAt', 'Creado')}
          render={(record: Group) => (
            <Typography variant="body2" color="text.secondary">
              {new Date(record.createdAt).toLocaleDateString()}
            </Typography>
          )}
          sortable={true}
        />
      </CustomDataGrid>
      </Box>
    </List>
  );
};

export default GroupList;